import React from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/AuthContext';

/**
 * Task: profile completeness gate (2026-09-15).
 *
 * Server-side enforcement is the real gate (worker/middleware/
 * profileCompleteness.ts, checked on POST /api/badge/scan and POST
 * /api/contacts): this component cannot be the actual security boundary,
 * since nothing stops a future client from calling the API directly. What
 * this adds is the UX the task asked for: block at app entry, before a rep
 * ever gets to a capture screen, rather than letting them scan a badge and
 * only then discover the save is refused.
 *
 * user.profile_complete and user.missing_profile_fields arrive on every
 * response that already returns a user (worker/types.ts's toPublicUser),
 * so this needs no separate fetch: the same login response that got a rep
 * into the app already says whether they can use it yet.
 */

/**
 * Exported so the admin reps list (Admin.jsx, task: missing-field
 * indicators, 2026-09-15) can label the exact same five fields the same
 * way, rather than keeping a second label map that could drift from this
 * one.
 */
export const FIELD_LABELS = {
  full_name: 'Name',
  title: 'Job title',
  email: 'Email',
  cell_phone: 'Cell phone',
  headshot: 'Headshot',
};

export default function ProfileCompletenessGate() {
  const { user, isAdmin } = useAuth();
  const location = useLocation();

  const missing = user?.missing_profile_fields ?? [];
  // is_device_account bypass (bug fix, 2026-09-15): a shared booth device
  // has no personal job title, cell phone or headshot by design, there is
  // no profile for it to ever complete. worker/middleware/profileCompleteness.ts's
  // checkCaptureAllowed already exempted it server side; this component
  // never checked the field at all, so a device account could never get
  // past this screen even though the actual capture endpoints would have
  // let it through.
  const blocked = !!user && !isAdmin && !user.is_device_account && missing.length > 0;

  // /profile has to stay reachable no matter what, or a blocked rep would
  // have no path to fix the very thing blocking them.
  if (blocked && location.pathname !== '/profile') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4 safe-top">
        <div className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-sm p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="text-lg font-bold text-foreground mb-1.5">Finish your profile first</h1>
          <p className="text-sm text-muted-foreground mb-4">
            A few things are missing before you can start capturing leads.
          </p>
          <ul className="space-y-1.5 mb-5 text-left">
            {missing.map((field) => (
              <li
                key={field}
                className="flex items-center gap-2 text-sm text-foreground bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-600 shrink-0" />
                {FIELD_LABELS[field] ?? field}
              </li>
            ))}
          </ul>
          <Link to="/profile">
            <Button className="w-full min-h-10">
              Go to your profile
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
